import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'
import React, { Fragment, useCallback, useState } from 'react'
import { EntityTypeMetadata, PackageFeatureResource, PackageUpdateInput } from '../http/api'
import { FeatureTypes } from '../types'
import FeatureDashboard from './FeatureDashboard'
import FeatureLabel from './FeatureLabel'
import Markdown from './Markdown'
import PerspectiveSelect from './PerspectiveSelect'
import { CardContent, Readme, Row } from './styles'
import { FeatureStatusTag } from './Tag'
import ToggleChevronButton from './ToggleChevronButton'
interface FeaturesCardProps {
    features: PackageFeatureResource[]
    type: string
    pkgOriginallyEnabled: boolean
    metadata: EntityTypeMetadata
    form: PackageUpdateInput
    setFeatureEnabled: (uuid: string, enabled: boolean) => void
    setFeatureSettings: (uuid: string, settings: Record<string, unknown>) => void
}

const countEnabledDashboards = (form: PackageUpdateInput, features: PackageFeatureResource[]) => {
    return Object.entries(form.featuresEnabled ?? []).filter(
        ([uuid, enabled]) => enabled && features.some(dashboard => dashboard.uuid === uuid)
    ).length
}

const showPerspectiveWarning = (form: PackageUpdateInput, features: PackageFeatureResource[]): boolean => {
    return !Object.entries(form.featuresSettings ?? [])
        .filter(([uuid]) => features.some(feature => feature.uuid === uuid))
        .every(([, setting]) => setting?.perspectiveId)
}

const FeaturesCard: React.VFC<FeaturesCardProps> = ({
    type,
    features,
    metadata,
    form,
    pkgOriginallyEnabled,
    setFeatureEnabled,
    setFeatureSettings
}) => {
    if (!features.length) {
        return null
    }

    const numberOfEnabledDashboards = countEnabledDashboards(form, features)

    // @ts-ignore
    const requiresPerspective = metadata?.settingsSchema?.properties?.perspectiveId != null

    const commonsProps = {
        form,
        features,
        pkgOriginallyEnabled,
        setFeatureEnabled,
        setFeatureSettings
    }

    return (
        <CardContent>
            <h3 className="title">
                {i18n('feature_type_card_title.' + type)} ({numberOfEnabledDashboards}/{features.length})&nbsp;
                {metadata?.entityTypeUrl ? (
                    <a href={metadata.entityTypeUrl} target="_blank">
                        <i className="fa fa-external-link" />
                    </a>
                ) : null}
            </h3>

            {requiresPerspective && showPerspectiveWarning(form, features) && (
                <div className="feature-warning">{i18n('To enable this feature, first select a perspective')}.</div>
            )}

            <div className="feature-table">
                {type === FeatureTypes.DASHBOARD && <FeatureDashboard {...commonsProps} />}

                {type !== FeatureTypes.DASHBOARD && !requiresPerspective && <WrapperFeatureItem {...commonsProps} />}

                {type !== FeatureTypes.DASHBOARD && requiresPerspective && (
                    <WrapperFeatureItemWithPerspective {...commonsProps} />
                )}
            </div>
        </CardContent>
    )
}

export default FeaturesCard

interface WrapperFeatureItemProps {
    features: PackageFeatureResource[]
    form: FeaturesCardProps['form']
    pkgOriginallyEnabled: boolean
    setFeatureEnabled: FeaturesCardProps['setFeatureEnabled']
    setFeatureSettings: FeaturesCardProps['setFeatureSettings']
}

const WrapperFeatureItem = (props: WrapperFeatureItemProps): JSX.Element => {
    return (
        <>
            <div className="feature-table-head">
                <div className="col-md-9"> {i18n('Name')}</div>
                <div className="col-md-3"> {i18n('Active')}</div>
            </div>

            <div className="feature-table-body">
                {props?.features?.map(feature => {
                    const propsFeature: FeatureItemProps = { ...props, feature }

                    return <FeatureItem key={feature.uuid} {...propsFeature} />
                })}
            </div>
        </>
    )
}

interface FeatureItemProps {
    feature: PackageFeatureResource
    form: FeaturesCardProps['form']
    pkgOriginallyEnabled: boolean
    setFeatureEnabled: FeaturesCardProps['setFeatureEnabled']
    setFeatureSettings: FeaturesCardProps['setFeatureSettings']
}

const FeatureItem: React.VFC<FeatureItemProps> = ({ feature, form, pkgOriginallyEnabled, setFeatureEnabled }) => {
    const [showDetails, setShowDetails] = useState(false)

    const enabled = form.featuresEnabled?.[feature.uuid] || false

    const handleShowDetails = useCallback(() => {
        setShowDetails(showDetails => !showDetails)
    }, [])

    const handleToggle = useCallback(() => {
        setFeatureEnabled(feature.uuid, !enabled)
    }, [enabled, feature.uuid, setFeatureEnabled])

    return (
        <>
            <Row key={feature.uuid} showDetails={showDetails}>
                <div className="col-md-9 feature-title">
                    <FeatureLabel feature={feature} />
                </div>
                <div className="col-md-3">
                    <ActionToggle active={enabled} onChange={handleToggle} />
                    <FeatureStatusTag pkgOriginallyEnabled={pkgOriginallyEnabled} feature={feature} />
                    &nbsp;
                </div>
                {feature.readme && <ToggleChevronButton toggle={showDetails} onClick={handleShowDetails} />}
            </Row>
            {showDetails && feature.readme && (
                <Readme className="clearfix">
                    <Markdown headingStartLevel={4} text={feature.readme} />
                </Readme>
            )}
        </>
    )
}

const WrapperFeatureItemWithPerspective = (props: WrapperFeatureItemProps): JSX.Element => {
    return (
        <>
            <div className="feature-table-head">
                <div className="col-md-5"> {i18n('Name')}</div>
                <div className="col-md-4"> {i18n('Perspective')}</div>
                <div className="col-md-2"> {i18n('Status')}</div>
                <div className="col-md-1" />
            </div>

            <div className="feature-table-body">
                {props?.features?.map(feature => {
                    const propsFeature: FeatureItemProps = { ...props, feature }

                    return <FeatureItemWithPerspective key={feature.uuid} {...propsFeature} />
                })}
            </div>
        </>
    )
}

const FeatureItemWithPerspective: React.VFC<FeatureItemProps> = ({
    feature,
    form,
    pkgOriginallyEnabled,
    setFeatureEnabled,
    setFeatureSettings
}) => {
    const [showDetails, setShowDetails] = useState(false)

    const enabled = form.featuresEnabled?.[feature.uuid] || false

    const handleShowDetails = useCallback(() => {
        setShowDetails(showDetails => !showDetails)
    }, [])

    const handlePerspectiveChange = useCallback(
        selectedId => {
            setFeatureSettings(
                feature.uuid,
                selectedId == null ? null : { perspectiveId: Number.parseInt(selectedId, 10) }
            )
        },
        [feature.uuid, setFeatureSettings]
    )

    const handleToggle = useCallback(() => {
        setFeatureEnabled(feature.uuid, !enabled)
    }, [enabled, feature.uuid, setFeatureEnabled])

    // use local state or from server
    const perspectiveId = form.featuresSettings?.[feature.uuid]?.perspectiveId ?? feature.settings?.perspectiveId

    return (
        <>
            <Row className="feature-table-row" key={feature.uuid} showDetails={showDetails}>
                <div className="col-md-5 feature-title">
                    <FeatureLabel feature={feature} />
                </div>
                <div className="col-md-4 perspective">
                    <PerspectiveSelect value={perspectiveId?.toString()} onChange={handlePerspectiveChange} />
                    {perspectiveId ? null : <i className="far fa-exclamation-triangle" />}
                </div>
                <div className="col-md-2">
                    <ActionToggle active={enabled} disabled={!perspectiveId} onChange={handleToggle} />

                    <FeatureStatusTag pkgOriginallyEnabled={pkgOriginallyEnabled} feature={feature} />
                </div>

                {feature.readme && (
                    <>
                        <ToggleChevronButton toggle={showDetails} onClick={handleShowDetails} />
                    </>
                )}
            </Row>
            {showDetails && feature.readme && (
                <Readme className="clearfix">
                    <Markdown headingStartLevel={4} text={feature.readme} />
                </Readme>
            )}
        </>
    )
}
